'use client'

import { IconPencil, IconWallet } from '@tabler/icons-react'
import { useRouter } from 'next/navigation'

import Button from 'shared/ui/Button'
import { Modal, ModalTitle } from 'shared/ui/Modal'
import { theme } from 'shared/ui/utils/designTokens'
import { getTimeFromDate, parseDateToDDMMYYYY } from 'shared/utils/date'

import { SaleOrder } from '../../../types'
import { useBeginEditSaleOrder } from '../hooks/useBeginEditSaleOrder'
import { useEditSaleOrderModal } from '../hooks/useEditSaleOrderModal'

import styles from './EditSaleOrderButton.module.scss'

type EditOrderModalProps = {
  order: SaleOrder
  onClose: () => void
  isLoading: boolean
  onSubmit: () => void
}

const EditOrderModal = ({
  order,
  onClose,
  isLoading,
  onSubmit
}: EditOrderModalProps) => (
  <>
    <div className={styles.listWrapper}>
      <OrderEditSection
        icon={<IconPencil size={20} color={theme.colors.brand.secondary} />}
        title='Edycja zamówienia'
      >
        <EditOrderDescription order={order} />
      </OrderEditSection>

      <OrderEditSection
        icon={<IconWallet size={20} color={theme.colors.brand.secondary} />}
        title='Przeliczenie cen'
      >
        <p className={styles.listItemDescription}>
          Ceny za produkty są przeliczane zgodnie z aktualnymi stawkami
          obowiązującymi na dzień dzisiejszy.
        </p>
      </OrderEditSection>
    </div>

    <div className={styles.buttonsWrapper}>
      <Button
        id='repeat_order_add_products_button'
        fullWidth
        variant='primary'
        loading={isLoading}
        disabled={isLoading}
        onClick={onSubmit}
      >
        Zatwierdź
      </Button>
      <Button
        id='repeat_order_cancel_button'
        fullWidth
        variant='neutralOutline'
        onClick={onClose}
      >
        Anuluj
      </Button>
    </div>
  </>
)

const OrderEditSection = ({
  icon,
  title,
  children
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) => (
  <div className={styles.listItemWrapper}>
    <div className={styles.listItemTitle}>
      {icon} <h2>{title}</h2>
    </div>
    {children}
  </div>
)

const EditOrderDescription = ({ order }: { order: SaleOrder }) => (
  <div className={styles.editOrderDescriptionWrapper}>
    <p>Możliwość edytowania zamówienia jest dostępna do:</p>
    {order.editableUntil && (
      <p>
        <strong>
          {parseDateToDDMMYYYY(order.editableUntil)}{' '}
          {getTimeFromDate(order.editableUntil)}
        </strong>
      </p>
    )}
    <p>Po tym czasie nie będzie możliwe wprowadzanie zmian.</p>
    <p>Zamówienie można edytować tylko jeden raz.</p>
    <p>Brak możliwości zmiany metody płatności.</p>
  </div>
)

interface EditSaleOrderButtonProps {
  size?: 'small' | 'large'
  order: SaleOrder
}

const EditSaleOrderButton = ({ size = 'large', order }: EditSaleOrderButtonProps) => {
  const router = useRouter()

  const {
    editStartModalOpen,
    handleEditStartModalClose,
    handleEditStartModalOpen
  } = useEditSaleOrderModal()
  const { handleEditSaleOrder, openingIsLoading } = useBeginEditSaleOrder()

  const handleSubmit = async () => {
    const hasEditStarder = await handleEditSaleOrder(order)

    handleEditStartModalClose()
    if (hasEditStarder) router.push('/koszyk')
  }

  const TriggerButton =
    size === 'large' ? (
      <Button
        id='repeat_order_repeat_button'
        variant='primary'
        startIcon={<IconPencil />}
        onClick={handleEditStartModalOpen}
        size='editOrder'
      >
        Edytuj zamówienie
      </Button>
    ) : (
      <IconPencil size={25} onClick={handleEditStartModalOpen} />
    )

  return (
    <>
      {TriggerButton}
      <Modal
        id='edit-order-modal'
        open={editStartModalOpen}
        onClose={handleEditStartModalClose}
      >
        <ModalTitle>Edycja zamówienia</ModalTitle>
        <EditOrderModal
          order={order}
          onClose={handleEditStartModalClose}
          isLoading={openingIsLoading}
          onSubmit={handleSubmit}
        />
      </Modal>
    </>
  )
}

export default EditSaleOrderButton
